// src/features/simulators/shared/components/SimulationProgress.tsx

import React from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  RadioButtonUnchecked as PendingIcon,
  Api as ApiIcon,
} from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import { MyFormSection } from '@/components/forms/MyFormLayout';
import { ApiRequestLog } from './ApiRequestLog';
import type { SimulationStatus, SimulationPhase } from '../hooks/useSimulatorWizard';
import type { CapturedRequest } from '../types/simulator.types';

interface SimulationProgressProps {
  status: SimulationStatus;
  phases: SimulationPhase[];
  currentLabel?: string;
  capturedRequests?: CapturedRequest[];
  simulatorName?: string;
}

export const SimulationProgress: React.FC<SimulationProgressProps> = ({
  status,
  phases,
  currentLabel,
  capturedRequests,
  simulatorName,
}) => {
  const translate = useTranslate();
  const theme = useTheme();

  const completedCount = phases.filter(p => p.status === 'completed').length;
  const progress = phases.length > 0 ? Math.round((completedCount / phases.length) * 100) : 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 3 }}>
      {/* Centered progress indicator + phases */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        py: 4,
      }}>
        {/* Circular Progress */}
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <CircularProgress
            variant={status === 'preparing' ? 'indeterminate' : 'determinate'}
            value={progress}
            size={100}
            thickness={3}
            sx={{ color: theme.palette.primary.main }}
          />
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
              {status === 'preparing' ? '...' : `${progress}%`}
            </Typography>
          </Box>
        </Box>

        {/* Status text */}
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
          {currentLabel || translate('resources.simulators.wizard.running_test')}
        </Typography>

        {/* Phase list */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          width: '100%',
          maxWidth: 400,
        }}>
          {phases.map((phase, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 1,
                px: 2,
                borderRadius: 1,
                backgroundColor: phase.status === 'active'
                  ? `${theme.palette.primary.main}08`
                  : 'transparent',
              }}
            >
              {phase.status === 'completed' && (
                <CheckIcon sx={{ color: theme.palette.success.main, fontSize: 22 }} />
              )}
              {phase.status === 'active' && (
                <CircularProgress size={18} thickness={4} sx={{ color: theme.palette.primary.main }} />
              )}
              {phase.status === 'pending' && (
                <PendingIcon sx={{ color: theme.palette.text.disabled, fontSize: 22 }} />
              )}
              {phase.status === 'failed' && (
                <ErrorIcon sx={{ color: theme.palette.error.main, fontSize: 22 }} />
              )}

              <Typography
                variant="body2"
                sx={{
                  color: phase.status === 'pending'
                    ? theme.palette.text.disabled
                    : theme.palette.text.primary,
                  fontWeight: phase.status === 'active' ? 600 : 400,
                }}
              >
                {translate(phase.label)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Live API Request Log */}
      {capturedRequests && capturedRequests.length > 0 && (
        <MyFormSection
          title={translate('resources.simulators.wizard.api_log_title')}
          subtitle={translate('resources.simulators.wizard.api_log_live')}
          icon={<ApiIcon />}
          variant="card"
          collapsible
          defaultExpanded
        >
          <ApiRequestLog
            requests={capturedRequests}
            simulatorName={simulatorName}
            autoScroll
          />
        </MyFormSection>
      )}
    </Box>
  );
};
